import { Tag } from 'lucide-react';
import { useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import type { ShellContext } from '@/components/layout/AppShell';
import { LabelChip } from '@/components/tasks/LabelChip';
import { TaskList } from '@/components/tasks/TaskList';
import { sortTasks, TaskSortBar, useTaskSort } from '@/components/tasks/TaskSort';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { useLabels } from '@/hooks/useLabels';
import { tasks as tasksApi } from '@/lib/api/endpoints';
import { toDayString } from '@/lib/dates';
import { useT } from '@/lib/i18n';
import { keys } from '@/lib/queryClient';
import { Page } from '@/routes/Today';
import { useTaskActions } from '@/routes/useTaskActions';

/** Everything you filed under one label. */
export function LabelRoute() {
  const t = useT();
  const { labelId = '' } = useParams();
  const { currentUser, openTask } = useOutletContext<ShellContext>();
  const actions = useTaskActions(openTask);
  const [sort, setSort] = useTaskSort('label');
  const [offset] = useState(0);

  const { data: labels } = useLabels();
  const label = (labels ?? []).find((entry) => entry.id === labelId);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: keys.byLabel(labelId, offset),
    queryFn: () => tasksApi.byLabel(labelId, { offset }),
    enabled: Boolean(labelId),
  });

  if (isPending)
    return (
      <Page>
        <LoadingState />
      </Page>
    );
  if (error)
    return (
      <Page>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Page>
    );
  if (!data) return null;

  return (
    <Page>
      <header className="mb-5 flex items-baseline gap-2.5">
        <h1 className="text-xl font-semibold tracking-tight">
          {label ? label.name : t('label.singular')}
        </h1>
        {label ? <LabelChip label={label} size="md" /> : null}
        {data.meta.total > 0 ? (
          <span className="text-sm tabular-nums text-content-subtle">
            {data.meta.total}
          </span>
        ) : null}
      </header>

      {data.items.length > 1 ? (
        <div className="mb-3">
          <TaskSortBar sort={sort} onChange={setSort} />
        </div>
      ) : null}

      {data.items.length === 0 ? (
        <EmptyState
          icon={<Tag className="h-5 w-5" />}
          title={t('label.empty.title')}
          hint={t('label.empty.hint')}
        />
      ) : (
        <TaskList
          tasks={sortTasks(data.items, sort)}
          today={toDayString(new Date())}
          currentUserId={currentUser.id}
          actions={actions}
          showDate
        />
      )}
    </Page>
  );
}
